import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Check, Loader2 } from 'lucide-react';
import { Button, FieldLabel, INK, Input } from '../../abf/onboarding/review/reviewStyles';
import type { AgreementRow } from './agreementReviewModel';

const Form = styled.form`
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr);
  gap: 0.6rem;
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px dashed ${INK.line};
  .actions { grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; }
  .problem { color: #fca5a5; font-size: 0.74rem; }
  .hint { color: ${INK.muted}; font-size: 0.72rem; }
`;

interface Props {
  row: AgreementRow;
  pageCount: number | null;
  onConfirm: (page: number | null, clause: string | null) => Promise<void>;
}

/** Point a value at where the agreement states it. Records the source; never changes the value. */
const SourceConfirmForm: React.FC<Props> = ({ row, pageCount, onConfirm }) => {
  const [page, setPage] = useState('');
  const [clause, setClause] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Start from what is already cited, so confirming a good citation is one click.
  useEffect(() => {
    setPage(row.citation.page != null ? String(row.citation.page) : '');
    setClause(row.citation.clause || row.citation.section || '');
    setProblem(null);
  }, [row.uid, row.citation.page, row.citation.clause, row.citation.section]);

  const pageNumber = page.trim() ? Number(page) : null;
  const pageValid = pageNumber == null
    || (Number.isInteger(pageNumber) && pageNumber >= 1 && (pageCount == null || pageNumber <= pageCount));
  const unchanged = row.citation.status === 'CONFIRMED' && pageNumber === row.citation.page
    && clause.trim() === (row.citation.clause || '');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pageValid) { setProblem(pageCount ? `The agreement has ${pageCount} pages.` : 'Enter a whole page number.'); return; }
    if (pageNumber == null && !clause.trim()) { setProblem('Give the page, the section or clause, or both.'); return; }
    setSaving(true); setProblem(null);
    try { await onConfirm(pageNumber, clause.trim() || null); }
    catch (failure) { setProblem(failure instanceof Error ? failure.message : 'The source was not saved.'); }
    finally { setSaving(false); }
  };

  return (
    <Form onSubmit={submit} aria-label={`Source for ${row.label}`}>
      <FieldLabel htmlFor="source-page">
        Page
        <Input id="source-page" inputMode="numeric" value={page} placeholder={pageCount ? `1–${pageCount}` : undefined}
          aria-invalid={!pageValid} onChange={event => setPage(event.target.value.replace(/[^\d]/g, ''))} />
      </FieldLabel>
      <FieldLabel htmlFor="source-clause">
        Section or clause
        <Input id="source-clause" value={clause} maxLength={300} onChange={event => setClause(event.target.value)} />
      </FieldLabel>
      <div className="actions">
        <Button type="submit" $variant="primary" disabled={saving || unchanged}>
          {saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />}
          {row.citation.status === 'CONFIRMED' ? 'Update the source' : 'Confirm with this source'}
        </Button>
        {problem ? <span className="problem" role="alert">{problem}</span>
          : <span className="hint">Records where the value is stated. The value itself does not change.</span>}
      </div>
    </Form>
  );
};

export default SourceConfirmForm;
